/**
 * Связанные задачи.
 *
 * §13.3 запрещает самоссылку, дубликаты и циклы — сервер отклоняет их
 * кодом CYCLE_DETECTED или DUPLICATE, и это разные сообщения.
 * §13.4: переход по связи открывает партнёрскую задачу, а удаление
 * убирает только связь, не задачу — формулировка в подтверждении важна.
 *
 * Диалог добавления — по макету: тип «Связана с» / «Зависит от», поиск
 * задачи в отделе, щелчок по найденной создаёт связь. Прежде у кнопки
 * «Добавить связь» не было обработчика.
 */

import { errorMessage, newRequestId, type WsError } from "@taskflow/client-core";
import { ErrorCode, TaskLinkType, TaskSection } from "@taskflow/contracts";
import { Link2Off } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  Button,
  cn,
  ConfirmDialog,
  ErrorState,
  FormField,
  IconButton,
  Input,
  Modal,
  Select,
  SkeletonRows,
  toast,
} from "@/shared/ui";
import { Plus } from "@/shared/ui/motionIcons";

import {
  type TaskLink,
  useCreateLinkMutation,
  useDeleteLinkMutation,
  useTaskLinksQuery,
  useTaskSearchQuery,
} from "@taskflow/client-core/tasks";
import { reportTaskPath, reportTaskSection } from "../reportTaskPath";
import { Section } from "./Section";

export function TaskLinks({
  taskKey,
  departmentId,
  canManage,
}: {
  taskKey: string;
  /** Подразделение доски: в нём ищутся задачи для связи. */
  departmentId: string | null;
  canManage: boolean;
}) {
  const links = useTaskLinksQuery(taskKey);
  const location = useLocation();
  const { boardId: currentBoardId } = useParams<{ boardId: string }>();
  const [remove, { isLoading: removing }] = useDeleteLinkMutation();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Адрес строится целиком: прежний относительный «../tasks/KEY» от
  // /boards/X/tasks/KEY давал /boards/X/tasks/tasks/KEY — переход вёл
  // в никуда. Из отчёта задача открывается в том же разделе отчёта
  // (§25.7), иначе — на своей доске; фильтры адреса сохраняются, пока
  // доска та же.
  const partnerUrl = (link: TaskLink) => {
    const reportSection = reportTaskSection(location.pathname);
    if (reportSection !== null) {
      return { pathname: reportTaskPath(reportSection, link.partnerKey), search: location.search };
    }
    return {
      pathname: `/boards/${link.partnerBoardId}/tasks/${link.partnerKey}`,
      search: link.partnerBoardId === currentBoardId ? location.search : "",
    };
  };

  const confirmRemove = async () => {
    if (confirmId === null) return;
    try {
      await remove({ key: taskKey, linkId: confirmId }).unwrap();
      setConfirmId(null);
      toast.success("Связь удалена");
    } catch (raw) {
      const error = raw as WsError;
      toast.error(
        error.data.code === ErrorCode.CYCLE_DETECTED
          ? "Связь создаёт цикл между задачами"
          : error,
      );
    }
  };

  return (
    <Section
      title="Связанные задачи"
      section={TaskSection.LINKS}
      count={links.data?.data.length}
      action={
        canManage ? (
          <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>
            Добавить связь
          </Button>
        ) : undefined
      }
    >
      {links.isLoading && <SkeletonRows rows={2} />}

      {links.error !== undefined && (
        <ErrorState
          message={(links.error as WsError).data.message}
          onRetry={() => void links.refetch()}
        />
      )}

      {links.data !== undefined &&
        (links.data.data.length === 0 ? (
          <p className="text-[13px] text-faint">Связей нет</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {links.data.data.map((link) => (
              <li
                key={link.id}
                // Полоса слева — выполнена ли связанная задача: зелёная — да,
                // жёлтая — ещё в работе. Цвет дублирует скрытая подпись.
                data-done={link.partnerDone}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border border-l-[3px] border-line px-2.5 py-2",
                  link.partnerDone ? "border-l-success" : "border-l-warning",
                )}
              >
                <span className="shrink-0 rounded-sm bg-sunken px-1.5 py-0.5 text-[11px] font-semibold text-muted">
                  {link.typeLabel}
                </span>
                <Link to={partnerUrl(link)} className="min-w-0 flex-1">
                  <span className="font-mono text-[11px] font-semibold text-faint">
                    {link.partnerKey}
                  </span>
                  <p className="truncate text-[13px] text-fg hover:underline">
                    {link.partnerTitle}
                  </p>
                </Link>
                <span
                  className={cn(
                    "shrink-0 text-[11px] font-semibold",
                    link.partnerDone ? "text-success" : "text-warning",
                  )}
                  title={link.partnerDone ? "Выполнена" : "Не выполнена"}
                >
                  {link.partnerArchived ? "в архиве" : link.partnerColumnName}
                  <span className="sr-only">
                    {link.partnerDone ? ", выполнена" : ", не выполнена"}
                  </span>
                </span>
                {canManage && (
                  <IconButton
                    icon={Link2Off}
                    label={`Убрать связь с ${link.partnerKey}`}
                    size="sm"
                    variant="danger"
                    onClick={() => setConfirmId(link.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        ))}

      {adding && (
        <AddLinkDialog
          taskKey={taskKey}
          departmentId={departmentId}
          links={links.data?.data ?? []}
          onClose={() => setAdding(false)}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Убрать связь"
        // §13.4: удаляется связь, не задача. Формулировка снимает страх.
        description="Будет удалена только связь между задачами. Сами задачи останутся на месте."
        confirmLabel="Убрать связь"
        loading={removing}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setConfirmId(null)}
      />
    </Section>
  );
}

/** Два типа, как в макете. Коды — из контракта, подписи — как в списке связей. */
const LINK_OPTIONS = [
  { id: TaskLinkType.RELATES_TO, label: "Связана с" },
  { id: TaskLinkType.BLOCKED_BY, label: "Зависит от" },
];

function AddLinkDialog({
  taskKey,
  departmentId,
  links,
  onClose,
}: {
  taskKey: string;
  departmentId: string | null;
  links: TaskLink[];
  onClose: () => void;
}) {
  const [create, { isLoading }] = useCreateLinkMutation();
  const [type, setType] = useState<string>(TaskLinkType.RELATES_TO);
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Запрос не на каждый символ: поиск уходит, когда ввод на миг остановился.
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const found = useTaskSearchQuery(
    { departmentId: departmentId ?? "", search: needle },
    { skip: departmentId === null || needle.length < 2 },
  );
  const candidates = (found.data?.data ?? []).filter((item) => item.key !== taskKey).slice(0, 20);

  const add = async (partnerKey: string) => {
    // Проверки макета — по уже загруженному списку, до запроса.
    if (links.some((link) => link.partnerKey === partnerKey && link.type === type)) {
      setError("Такая связь уже существует.");
      return;
    }
    if (
      type === TaskLinkType.BLOCKED_BY &&
      links.some((link) => link.partnerKey === partnerKey && link.type === TaskLinkType.BLOCKS)
    ) {
      setError("Циклическая зависимость невозможна.");
      return;
    }
    setError(null);
    try {
      await create({
        key: taskKey,
        targetKey: partnerKey,
        type,
        clientRequestId: newRequestId(),
      }).unwrap();
      toast.success("Связь добавлена", partnerKey);
      onClose();
    } catch (raw) {
      const failure = raw as Partial<WsError>;
      // Цикл через цепочку задач виден только серверу.
      setError(
        failure.data?.code === ErrorCode.CYCLE_DETECTED
          ? "Циклическая зависимость невозможна."
          : errorMessage(raw),
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Добавить связь"
      width="sm"
      dismissable={!isLoading}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Отмена
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-muted" aria-hidden>
            Тип связи
          </span>
          <Select
            label="Тип связи"
            placeholder="Тип связи"
            options={LINK_OPTIONS}
            value={type}
            onChange={(next) => {
              setType(next);
              setError(null);
            }}
          />
        </div>

        <FormField label="Найти задачу (в этом отделе)">
          {(props) => (
            <Input
              {...props}
              value={search}
              autoFocus
              placeholder="Номер или название"
              onChange={(event) => setSearch(event.target.value)}
            />
          )}
        </FormField>

        {needle.length >= 2 && (
          <div className="max-h-[180px] overflow-y-auto">
            {found.isFetching && candidates.length === 0 ? (
              <p className="px-2.5 py-2 text-[12px] text-faint">Ищем…</p>
            ) : candidates.length === 0 ? (
              <p className="px-2.5 py-2 text-[12px] text-faint">Ничего не найдено</p>
            ) : (
              <ul className="flex flex-col gap-1" aria-label="Найденные задачи">
                {candidates.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => void add(item.key)}
                      className="w-full rounded-md px-2.5 py-2 text-left text-[12px] text-fg hover:bg-sunken focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
                    >
                      <span className="font-mono font-semibold text-faint">{item.key}</span>
                      {" · "}
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error !== null && (
          <p role="alert" className="text-[12px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
